import React, { useState, useMemo, useCallback } from "react";
import { Spinner } from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { getKeyPersonnelProfileView } from "../company-api";
import { KeyPersonnelTable } from "./components/KeyPersonnelTable";
import { KeyPersonnelDetailModal } from "./components/KeyPersonnelDetailModal";
import { Pagination } from "newui/components/Pagination/Pagination";
import { MobilePersonnelCard } from "newui/features/Company/CompanyProfile/Preview/components/mobile/MobilePersonnelCard";
import type { KeyPersonnelTableItem } from "./components/KeyPersonnelTable";
import type { KeyPersonnelProfileViewItem } from "newui/features/Company/types/KeyPersonnelProfileView.type";

const DEFAULT_ROWS_PER_PAGE = 10;

interface KeyPersonnelProfileViewProps {
	companyId: number;
	path?: string;
}

const PROFILE_VIEW_KEYS = {
	list: (companyId: number) =>
		["company-profile-view", "key-personnel", companyId] as const,
};

const KeyPersonnelProfileView: React.FC<KeyPersonnelProfileViewProps> = ({
	companyId,
}) => {
	const [search, setSearch] = useState("");
	const [currentPage, setCurrentPage] = useState(1);
	const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
	const [selectedPerson, setSelectedPerson] = useState<KeyPersonnelProfileViewItem | null>(null);
	const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

	const { data: personnelData = {
		company_profile_type_id: 0,
		personnel: [],
	}, isLoading } = useQuery({
		queryKey: PROFILE_VIEW_KEYS.list(companyId),
		queryFn: () => getKeyPersonnelProfileView(companyId),
		enabled: companyId > 0,
	});

	const profileTypeId = personnelData.company_profile_type_id;
	const personnel = personnelData.personnel;

	const filtered = useMemo(() => {
		if (!search.trim()) {
			return personnel;
		}
		const term = search.toLowerCase();
		return personnel.filter(
			(p) =>
				p.full_name.toLowerCase().includes(term) ||
				p.position.toLowerCase().includes(term)
		);
	}, [personnel, search]);

	const handleSearchChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			setSearch(e.target.value);
			setCurrentPage(1);
		},
		[]
	);

	const paginatedData = useMemo(() => {
		const start = (currentPage - 1) * rowsPerPage;
		return filtered.slice(start, start + rowsPerPage);
	}, [filtered, currentPage, rowsPerPage]);

	const handlePageChange = useCallback((page: number) => {
		setCurrentPage(page);
	}, []);

	const handleRowsPerPageChange = useCallback((newRowsPerPage: number) => {
		setRowsPerPage(newRowsPerPage);
		setCurrentPage(1);
	}, []);

	const handleRowClick = useCallback((person: KeyPersonnelTableItem) => {
		const fullPerson = personnel.find((p) => p.id === person.id);
		if (fullPerson) {
			setSelectedPerson(fullPerson);
			setIsDetailModalOpen(true);
		}
	}, [personnel]);

	const handleCloseDetailModal = useCallback(() => {
		setIsDetailModalOpen(false);
		setSelectedPerson(null);
	}, []);

	if (isLoading) {
		return (
			<div
				className="flex items-center justify-center py-16"
				data-testid="profile-view-loading"
			>
				<Spinner size="lg" />
			</div>
		);
	}

	return (
		<div
			className="flex flex-col gap-4 p-4"
			data-testid="key-personnel-profile-view"
		>
			<div className="flex items-center justify-between">
				<h2 className="text-lg font-semibold text-gray-900">
					Key Personnel & Expertise
				</h2>
				<input
					type="text"
					value={search}
					onChange={handleSearchChange}
					placeholder="Search by name or position..."
					className="w-64 rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
					data-testid="profile-view-search"
				/>
			</div>

			{personnel.length === 0 ? (
				<div
					className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-200 py-16"
					data-testid="profile-view-empty"
				>
					<p className="text-lg font-medium text-gray-600">
						No key personnel listed
					</p>
				</div>
			) : (
				<>
					{/* Desktop: table */}
					<div className="hidden md:block">
						<KeyPersonnelTable
							personnel={paginatedData}
							mode="profile-view"
							profileTypeId={profileTypeId}
							onRowClick={handleRowClick}
						/>
					</div>

					{/* Mobile: card list */}
					<div className="flex flex-col gap-3 md:hidden">
						{paginatedData.map((person) => (
							<MobilePersonnelCard
								key={person.id}
								person={person}
								profileTypeId={profileTypeId}
								onClick={() => handleRowClick(person)}
							/>
						))}
					</div>

					<Pagination
						currentPage={currentPage}
						rowsPerPage={rowsPerPage}
						totalItems={filtered.length}
						onPageChange={handlePageChange}
						onRowsPerPageChange={handleRowsPerPageChange}
						showRowsPerPage
						rowsPerPageOptions={[10, 20, 50]}
						testId="profile-view-pagination"
					/>
				</>
			)}

			<KeyPersonnelDetailModal
				isOpen={isDetailModalOpen}
				onClose={handleCloseDetailModal}
				person={selectedPerson}
				profileTypeId={profileTypeId}
			/>
		</div>
	);
};

export { KeyPersonnelProfileView };
